import React from "react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { WQE } from "@/config/weeklyqe";

// Prose helper — same behavior as Four Levels Landing.
const Prose = ({ text, className = "" }) => {
  const paragraphs = text.split(/\n\n+/);
  const items = [];
  paragraphs.forEach((paragraph, pi) => {
    const sentences = paragraph.trim().split(/(?<=[.?!])\s+(?=["'A-Z])/);
    sentences.forEach((sentence, si) => {
      const isFirstOfLaterParagraph = pi > 0 && si === 0;
      items.push(
        <p key={`${pi}-${si}`} className={`${className} ${isFirstOfLaterParagraph ? "mt-7" : ""}`}>
          {sentence.split("\n").map((line, k, arr) => (
            <React.Fragment key={k}>
              {line}
              {k < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      );
    });
  });
  return items;
};

const Section = ({ children, eyebrow, testId }) => (
  <section className="border-t border-border/40" data-testid={testId}>
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-20">
      {eyebrow && (
        <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground mb-6">{eyebrow}</p>
      )}
      {children}
    </div>
  </section>
);

export default function WeeklyQELanding() {
  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-brand-turquoise/25">
      {/* Sticky nav */}
      <header
        className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border/50"
        data-testid="wqe-nav"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-4">
          <a href={WQE.fourLevelsUrl} className="flex items-center gap-3 min-w-0" data-testid="wqe-brand-logo">
            <img src={WQE.logoUrl} alt={WQE.parentBrand} className="h-8 md:h-9 w-auto" />
          </a>
          <Button
            asChild
            variant="ghost"
            className="rounded-full text-sm text-foreground/80 hover:text-primary hover:bg-transparent shrink-0"
            data-testid="wqe-nav-cta"
          >
            <a href={WQE.paymentLinkUrl}>Continue</a>
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative">
        <div className="max-w-3xl mx-auto px-6 md:px-10 pt-14 md:pt-28 pb-12 md:pb-20">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="text-xs uppercase tracking-[0.24em] text-brand-turquoise"
            data-testid="wqe-hero-eyebrow"
          >
            An invitation to continue
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.08] tracking-tight mt-6 max-w-[22ch]"
            data-testid="wqe-hero-headline"
          >
            {WQE.hero.headline.split(" ").slice(0, -1).join(" ")}{" "}
            <span className="italic text-brand-turquoise">{WQE.hero.headline.split(" ").slice(-1)}</span>
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 md:mt-12 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[62ch]"
            data-testid="wqe-hero-body"
          >
            <Prose text={WQE.hero.body} />
          </motion.div>
          <div className="mt-12 md:mt-14">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-primary hover:bg-primary text-primary-foreground btn-lift px-7 h-12 text-base"
              data-testid="wqe-hero-cta"
            >
              <a href={WQE.paymentLinkUrl}>
                {WQE.hero.cta} <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Why */}
      <Section eyebrow={WQE.why.eyebrow} testId="wqe-why-section">
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.15] max-w-[28ch]">
          {WQE.why.headline}
        </h2>
        <div className="mt-8 md:mt-10 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[62ch]">
          <Prose text={WQE.why.body} />
        </div>
      </Section>

      {/* Week */}
      <Section eyebrow={WQE.week.eyebrow} testId="wqe-week-section">
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.15] max-w-[26ch]">
          {WQE.week.headline}
        </h2>
        <p className="mt-8 md:mt-10 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[62ch]">
          {WQE.week.body}
        </p>
        <ul className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl">
          {WQE.week.activities.map((a, i) => (
            <li
              key={i}
              className="flex items-baseline gap-3 text-[15px] md:text-base text-foreground/80"
              data-testid={`wqe-week-item-${i}`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-brand-green shrink-0 translate-y-1.5" />
              {a}
            </li>
          ))}
        </ul>
        <div className="mt-10 md:mt-12 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[62ch]">
          <Prose text={WQE.week.closing} />
        </div>
      </Section>

      {/* Fit */}
      <Section eyebrow={WQE.fit.eyebrow} testId="wqe-fit-section">
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.15] max-w-[24ch]">
          {WQE.fit.headline}
        </h2>
        <ul className="mt-10 md:mt-12 space-y-6 max-w-[62ch]">
          {WQE.fit.lines.map((l, i) => (
            <li
              key={i}
              className="text-[17px] md:text-lg leading-[1.75] text-foreground/85 pl-6 border-l-2 border-brand-turquoise/40"
              data-testid={`wqe-fit-line-${i}`}
            >
              {l}
            </li>
          ))}
        </ul>
        <p className="mt-12 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[52ch]">
          {WQE.fit.closing}
        </p>
      </Section>

      {/* Details */}
      <Section eyebrow={WQE.details.eyebrow} testId="wqe-details-section">
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.15] max-w-[24ch]">
          {WQE.details.headline}
        </h2>
        <dl className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-[10rem_1fr] gap-x-8 gap-y-4">
          {WQE.details.items.map((it, i) => (
            <React.Fragment key={i}>
              <dt className="text-xs uppercase tracking-[0.22em] text-brand-turquoise pt-1" data-testid={`wqe-detail-label-${i}`}>
                {it.label}
              </dt>
              <dd className="text-[16px] md:text-lg leading-[1.7] text-foreground/85 border-b border-border/40 pb-4">
                {it.value}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      </Section>

      {/* FAQ */}
      <Section eyebrow={WQE.faq.eyebrow} testId="wqe-faq-section">
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.15] max-w-[24ch]">
          {WQE.faq.headline}
        </h2>
        <dl className="mt-10 md:mt-12 divide-y divide-border/60 max-w-[62ch]">
          {WQE.faq.items.map((it, i) => (
            <div key={i} className="py-6" data-testid={`wqe-faq-item-${i}`}>
              <dt className="font-serif text-xl md:text-2xl tracking-tight leading-snug text-foreground">
                {it.q}
              </dt>
              <dd className="mt-3 text-[16px] md:text-lg leading-[1.7] text-foreground/80">
                {it.a}
              </dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* Continue */}
      <section id="continue" className="border-t border-border/40" data-testid="wqe-continue-section">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
          <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
            {WQE.continueSection.eyebrow}
          </p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-6 leading-[1.15] max-w-[28ch] mx-auto">
            {WQE.continueSection.headline}
          </h2>
          <p className="mt-8 md:mt-10 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[52ch] mx-auto">
            {WQE.continueSection.body}
          </p>
          <p className="mt-10 md:mt-12 font-serif text-3xl md:text-4xl text-primary" data-testid="wqe-price">
            {WQE.priceLabel}
          </p>
          <div className="mt-10 md:mt-12">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-primary hover:bg-primary text-primary-foreground btn-lift px-8 h-12 text-base"
              data-testid="wqe-continue-cta"
            >
              <a href={WQE.paymentLinkUrl}>
                {WQE.continueSection.cta} <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
          <p className="mt-6 text-sm text-muted-foreground">Cancel anytime · No refunds</p>
        </div>
      </section>

      {/* Questions */}
      <Section eyebrow={WQE.questions.eyebrow} testId="wqe-questions-section">
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.15] max-w-[30ch]">
          {WQE.questions.headline}
        </h2>
        <p className="mt-6 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[52ch]">
          {WQE.questions.body}
        </p>
        <div className="mt-10">
          <Button
            asChild
            variant="outline"
            className="rounded-full border-brand-turquoise text-brand-turquoise hover:bg-brand-turquoise/10 hover:text-brand-turquoise px-6 h-11"
            data-testid="wqe-questions-cta"
          >
            <a href={WQE.questions.ctaUrl} target="_blank" rel="noopener noreferrer">
              {WQE.questions.cta} <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </Section>

      {/* Closing */}
      <section className="border-t border-border/40 bg-muted/30" data-testid="wqe-closing-section">
        <div className="max-w-2xl mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
          <div className="font-serif text-xl md:text-2xl italic text-foreground/70 leading-relaxed space-y-4">
            <Prose text={WQE.closing} />
          </div>
          <p className="mt-10 text-sm text-muted-foreground">With warmth,<br />Dan Hoover · Quantum Results</p>
        </div>
      </section>

      <footer className="border-t border-border/40">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-8 flex flex-wrap justify-between gap-3 text-sm text-muted-foreground">
          <span data-testid="wqe-footer-brand">&copy; {new Date().getFullYear()} {WQE.parentBrand}</span>
          <a href={WQE.fourLevelsUrl} className="hover:text-primary transition-colors">Return to the Four Levels Experience</a>
        </div>
      </footer>
    </div>
  );
}
